import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Send, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { sendClerkEmail, withCurrentGreeting } from "@/lib/clerkEmail";
import { MAX_ATTACHMENT_BYTES } from "@/lib/disbursementEmail";
import { FINANCE_CC, FINANCE_TO, financeBody, financeSubject } from "@/lib/financeFeeSlipEmail";

const kb = (bytes) => `${Math.max(1, Math.round(bytes / 1024))} KB`;

// One email to VALS finance for the ticked "Send to Finance for Payment" fee slips, each
// listed in Emma's format with its invoice attached (src/lib/financeFeeSlipEmail.js).
// Nothing is sent until Send is pressed. `loadAttachments(row)` resolves to the row's PDFs
// as { filename, mimeType, data (base64), size }; `onSent` gets { to, cc }.
export default function FinanceFeeSlipEmail({ rows, signOff, loadAttachments, onSent, onClose }) {
  const [to, setTo] = useState(FINANCE_TO);
  const [cc, setCc] = useState(FINANCE_CC);
  const [subject, setSubject] = useState(() => financeSubject(rows));
  const [subjectEdited, setSubjectEdited] = useState(false);
  const [body, setBody] = useState(() => financeBody(rows, { signOff }));
  const [bodyEdited, setBodyEdited] = useState(false);
  const [files, setFiles] = useState({}); // row id -> { state: "loading"|"ok"|"none"|"error", list, note }
  const [sending, setSending] = useState(false);

  // Until changed by hand, the wording follows the ticked fee slips.
  const shownSubject = subjectEdited ? subject : financeSubject(rows);
  const shownBody = bodyEdited ? body : financeBody(rows, { signOff });

  const rowKey = rows.map((row) => row.id).join("|");
  useEffect(() => {
    let cancelled = false;
    setFiles(Object.fromEntries(rows.map((row) => [row.id, { state: "loading", list: [] }])));
    rows.forEach(async (row) => {
      let result;
      try {
        const list = await loadAttachments(row);
        result = list.length ? { state: "ok", list } : { state: "none", list: [], note: "No invoice found" };
      } catch (error) {
        result = { state: "error", list: [], note: error?.message || "Could not be read" };
      }
      if (!cancelled) setFiles((current) => ({ ...current, [row.id]: result }));
    });
    return () => { cancelled = true; };
    // Read once per set of ticked fee slips, not on every page refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKey]);

  const loading = rows.some((row) => !files[row.id] || files[row.id].state === "loading");
  const attachments = useMemo(() => rows.flatMap((row) => files[row.id]?.list || []), [rows, files]);
  const totalBytes = attachments.reduce((sum, file) => sum + (file.size || 0), 0);
  const missing = rows.filter((row) => files[row.id] && ["none", "error"].includes(files[row.id].state));
  const tooBig = totalBytes > MAX_ATTACHMENT_BYTES;

  const send = async () => {
    if (loading) return;
    if (tooBig) {
      toast.error("The invoices are too large to email together (3 MB at most). Untick some and send them in a second email.");
      return;
    }
    if (missing.length && !window.confirm(
      `${missing.length} fee slip${missing.length === 1 ? " has" : "s have"} no invoice to attach. Send without ${missing.length === 1 ? "it" : "them"}?`,
    )) return;
    setSending(true);
    try {
      await sendClerkEmail({
        to,
        cc,
        bcc: "",
        subject: shownSubject,
        body: withCurrentGreeting(shownBody),
        attachments: attachments.map(({ filename, mimeType, data }) => ({ filename, mimeType, data })),
      });
      toast.success(`Emailed ${to}${cc.trim() ? `, cc ${cc}` : ""}: ${rows.length} fee slip${rows.length === 1 ? "" : "s"}.`);
      onSent({ to, cc });
    } catch (error) {
      toast.error(error.message || "The email could not be sent.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3 border-y border-sky-200 bg-sky-50/60 p-4" aria-label="Email finance">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-bold text-slate-800">
          Email finance: {rows.length} fee slip{rows.length === 1 ? "" : "s"} to be paid from the Crim budget
        </p>
        <Button type="button" variant="ghost" onClick={onClose} className="h-7 px-2" aria-label="Close email to finance">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="finance-to" className="text-[10px] font-bold uppercase tracking-wide text-slate-500">To</Label>
          <Input id="finance-to" value={to} onChange={(event) => setTo(event.target.value)} className="mt-1 h-8 bg-white text-xs" />
        </div>
        <div>
          <Label htmlFor="finance-cc" className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Cc</Label>
          <Input id="finance-cc" value={cc} onChange={(event) => setCc(event.target.value)} className="mt-1 h-8 bg-white text-xs" />
        </div>
      </div>
      <div>
        <Label htmlFor="finance-subject" className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Subject</Label>
        <Input
          id="finance-subject"
          value={shownSubject}
          onChange={(event) => { setSubject(event.target.value); setSubjectEdited(true); }}
          className="mt-1 h-8 bg-white text-xs"
        />
      </div>
      <div>
        <Label htmlFor="finance-body" className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Message</Label>
        <Textarea
          id="finance-body"
          value={shownBody}
          onChange={(event) => { setBody(event.target.value); setBodyEdited(true); }}
          rows={Math.min(24, 8 + rows.length * 5)}
          className="mt-1 bg-white font-mono text-xs"
        />
      </div>
      <div className="space-y-1">
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
          Attachments {loading ? "(reading…)" : `(${attachments.length}, ${kb(totalBytes)})`}
        </p>
        <ul className="space-y-0.5">
          {rows.map((row) => {
            const entry = files[row.id];
            return (
              <li key={row.id} className="flex items-center gap-1.5 text-[11px]">
                <Paperclip className="h-3 w-3 text-slate-400" aria-hidden="true" />
                <span className="text-slate-700">{row.clientName}</span>
                <span className={entry?.state === "ok" ? "text-emerald-700" : entry?.state === "loading" || !entry ? "text-slate-400" : "text-rose-700"}>
                  {!entry || entry.state === "loading"
                    ? "reading…"
                    : entry.state === "ok"
                      ? entry.list.map((file) => file.filename).join(", ")
                      : entry.note}
                </span>
              </li>
            );
          })}
        </ul>
        {tooBig && (
          <p className="text-[11px] font-semibold text-rose-700">
            Over the 3 MB an email can carry. Untick some fee slips and send them in a second email.
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          onClick={send}
          disabled={sending || loading || tooBig || !rows.length}
          className="h-8 gap-1 bg-slate-800 px-4 text-[11px] text-white hover:bg-slate-900"
        >
          <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : loading ? "Reading invoices…" : "Send"}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose} className="h-8 px-3 text-[11px]">Cancel</Button>
      </div>
    </div>
  );
}
